import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import keyboardKey from 'keyboard-key';
import { injectIntl, FormattedMessage } from 'react-intl';
import { Grid, Icon } from '../../components';

import { handleClickTrackingSignUp } from '../../utils/tagmanager';
import { handleClickTrackingCollegeName } from '../../utils/tagmanager';

import { getCollegeEventDetailsClicked, getCollegeProfileLinkClicked, getRegistrationLinkClicked } from '../../utils/clicktrackingManager';

import messages from './messages';

class CollegeEventRow extends React.PureComponent {
  static propTypes = {
    intl: PropTypes.object,
    event: PropTypes.object.isRequired,
    handleClickTracking: PropTypes.func.isRequired,
  };

  state = {
    viewMore: false,
    showTooltip: false,
  };

  handleProfileLinkClick = () => {
    const { event, handleClickTracking, hasCoppaFerpaBlock } = this.props;
    if (hasCoppaFerpaBlock) return;
    handleClickTracking(getCollegeProfileLinkClicked(event));
    handleClickTrackingCollegeName();
  };

  handleSignUpClick = () => {
    const { event, handleClickTracking } = this.props;
    handleClickTracking(getRegistrationLinkClicked(event));
    handleClickTrackingSignUp();
  };

  handleViewMoreKey = (event) => {
    const key = keyboardKey.getCode(event);
    if (key !== keyboardKey.Spacebar && key !== keyboardKey.Enter) return;
    this.handleViewMoreClick();
    event.preventDefault();
  };

  handleViewLessKey = (event) => {
    const key = keyboardKey.getCode(event);
    if (key !== keyboardKey.Spacebar && key !== keyboardKey.Enter) return;
    this.setState({ viewMore: false });
    event.preventDefault();
  };

  handleViewMoreClick = () => {
    const { event, handleClickTracking, hasCoppaFerpaBlock } = this.props;
    const { viewMore } = this.state;
    this.setState({ viewMore: !viewMore });
    if (hasCoppaFerpaBlock) return;
    handleClickTracking(getCollegeEventDetailsClicked(event));
  };

  renderSignUpButton = (registrationUrl) => {
    if (this.props.hasCoppaFerpaBlock) return this.renderCoppaBlockedSignUpButton();
    return (
      <Grid>
        <Grid.Row>
          <Grid.Column>
            <div className="event-row-sign-up" data-cy="event-row-signup">
              <a href={registrationUrl} target="_blank" rel="noopener noreferrer" onClick={this.handleSignUpClick}>
                <FormattedMessage {...messages.signup} /> <Icon name="external share" />
              </a>
            </div>
          </Grid.Column>
        </Grid.Row>
      </Grid>
    );
  };

  renderCoppaBlockedSignUpButton = () => {
    const tooltipText = this.props.hasFerpaBlock ? messages.ferpaBlockText : messages.coppaBlockText;
    return (
      <Grid>
        <Grid.Row>
          <Grid.Column>
            <div
              className="event-row-sign-up-container"
              data-cy="event-row-signup"
              onMouseEnter={() => this.setState({ showTooltip: true })}
              onMouseLeave={() => this.setState({ showTooltip: false })}
            >
              {this.state.showTooltip && (
                <div className="event-row-sign-up-tooltip-container">
                  <div className="event-row-sign-up-tooltip" data-cy="event-row-sign-up-tooltip">
                    <div className="arrow" />
                    <div className="text">
                      <FormattedMessage {...tooltipText} />
                    </div>
                    <div className="hint">
                      <FormattedMessage {...messages.coppaFerpaBlockHint} />
                    </div>
                  </div>
                </div>
              )}
              <div className="event-row-sign-up coppa-block">
                <FormattedMessage {...messages.signup} /> <Icon name="external share" />
              </div>
            </div>
          </Grid.Column>
        </Grid.Row>
      </Grid>
    );
  };

  render() {
    const { event, intl } = this.props;
    const { viewMore } = this.state;
    const location = event.collegeEventLocation;
    const dateStart = moment.tz(event.eventDateTime, event.timezone);
    const dateEnd = moment.tz(event.eventEndDateTime, event.timezone);
    const timezone = moment.tz(event.timezone);

    const containsHttpOrHttps = /^https?:\/\//.test(event.registrationUrl);
    const registrationUrl = containsHttpOrHttps ? event.registrationUrl : 'http://' + event.registrationUrl;
    return (
      <Grid stackable>
        <Grid.Row className="event-f-row">
          <Grid.Column computer={2} mobile={12}>
            <Grid>
              <Grid.Row>
                <Grid.Column className="event-row-date-column" only="computer" width={15}>
                  <div className="event-row-date" data-cy="event-row-date">
                    <div>{dateStart.format('ddd')}</div>
                    <div className="day">{dateStart.format('MMM D')}</div>
                    <div className="hour">
                      {dateStart.format('h:mm A')} - {dateEnd.format('h:mm A')} {timezone.zoneAbbr()}
                    </div>
                  </div>
                </Grid.Column>
                <Grid.Column only="mobile" width={15}>
                  <div className="event-row-date" data-cy="event-row-date">
                    <div>
                      {dateStart.format('dddd')}, {dateStart.format('MMM D')}
                    </div>
                    <div className="hour">
                      {dateStart.format('h:mm A')} - {dateEnd.format('h:mm A')} {timezone.zoneAbbr()}
                    </div>
                  </div>
                </Grid.Column>
              </Grid.Row>
            </Grid>
          </Grid.Column>
          <Grid.Column width={8}>
            <Grid className="event-column-grid">
              <Grid.Row>
                <Grid.Column>
                  <div className="event-row-college-name" data-cy="event-row-college">
                    <a
                      href={`/colleges/hubs/${event.college.scid}`}
                      onClick={this.handleProfileLinkClick}
                      target="_blank"
                      rel="opener"
                      data-navi-donottranslate
                    >
                      {event.college.name}
                    </a>
                  </div>
                </Grid.Column>
              </Grid.Row>
              <Grid.Row>
                <Grid.Column data-cy="event-row-event">
                  <div className="event-row-name">
                    <a href={`/colleges/hubs/${event.college.scid}`} target="_blank" rel="opener">
                      {event.name}
                    </a>
                    {event.recommended && !this.props.hasCoppaFerpaBlock && (
                      <div className="event-row-recommended hide" data-cy="event-row-recommended">
                        <Icon name="flag" />
                        <FormattedMessage {...messages.recommended} />
                      </div>
                    )}
                  </div>
                  {event.eventDescription && !viewMore && (
                    <div
                      className="event-row-view more"
                      tabIndex="0"
                      data-cy="event-view-toggle-more"
                      onClick={this.handleViewMoreClick}
                      onKeyDown={this.handleViewMoreKey}
                    >
                      {intl.formatMessage(messages.moredetails)}
                    </div>
                  )}
                </Grid.Column>
              </Grid.Row>
            </Grid>
          </Grid.Column>
          <Grid.Column width={2}>
            <Grid>
              <Grid.Row>
                <Grid.Column mobile={7} computer={12} className="event-column-location">
                  {!event.virtual && location ? (
                    <div data-cy="event-row-location-physical">
                      <div className="event-row-location-name">{location.name}</div>
                      <div className="event-row-location-street">
                        {location.street} {location.street2}
                      </div>
                      <div className="event-row-location-city">
                        {location.city}, {location.state} {location.zipCode.zipCode}
                      </div>
                    </div>
                  ) : (
                    <div data-cy="event-row-location-virtual">
                      <div className="event-row-location-name">
                        <Icon name="computer" />
                        <FormattedMessage {...messages.virtualEvent} />
                      </div>
                    </div>
                  )}
                </Grid.Column>
                <Grid.Column only="mobile" mobile={7} className="event-column-recommended mobile">
                  {event.recommended && (
                    <div className="event-row-recommended mobile">
                      <Icon name="flag" />
                      <FormattedMessage {...messages.recommendedMobile} />
                    </div>
                  )}
                </Grid.Column>
              </Grid.Row>
            </Grid>
          </Grid.Column>
          <Grid.Column width={3} only="large screen">
            {this.renderSignUpButton(registrationUrl)}
          </Grid.Column>
        </Grid.Row>
        <Grid.Row className="event-s-row">
          {/* Used as offset */}
          <Grid.Column width={2} only="large screen" />
          <Grid.Column width={10}>
            <div className="event-row-description" data-cy="event-row-description">
              {viewMore && <div>{event.eventDescription}</div>}
              {event.eventDescription && (
                <div
                  tabIndex="0"
                  className="event-row-view"
                  onKeyDown={this.handleViewLessKey}
                  onClick={() => {
                    this.setState({ viewMore: !viewMore });
                  }}
                  data-cy="event-view-toggle-less"
                >
                  {viewMore ? intl.formatMessage(messages.fewerdetails) : ''}
                </div>
              )}
              {!event.eventDescription && (
                <div className="event-row-no-details">
                  <FormattedMessage {...messages.nodetails} />
                </div>
              )}
            </div>
          </Grid.Column>
          <Grid.Column only="mobile">{this.renderSignUpButton(registrationUrl)}</Grid.Column>
        </Grid.Row>
      </Grid>
    );
  }
}

export default injectIntl(CollegeEventRow);
